import { useMemo, useState } from 'react';
import type { ComponentCheck } from '../hooks/useTierEvaluation';
import { MachineCheckRow } from '../components/machineCheckRow';
import { ReachTierDetailCard } from '../components/reachTierDetailCard';
import { RewardsSubpageChrome } from '../components/rewardsSubpageChrome';
import { TIER_0_UNLOCK_INTRO_COPY } from './tier0UnlockShellChecks';
import { mergeUnlockChecks, type HostEligibilityCondition } from './eligibilityCheckRows';
import { TIER_0_WELCOME_HEADLINE } from './tier0WelcomeHubCopy';

/**
 * Purple → yellow outline for the Tier 0 reach card.
 */
export const REACH_TIER_0_BORDER_GRADIENT =
  'linear-gradient(135deg, #964FFF 0%, #FBBF24 100%)';

/**
 * Tier 0 reach card face wash: purple (top-left) → yellow (bottom-right),
 * using the same accent hexes as the other pages' washes
 * (purple #8B00FF / rgb(139,0,255), yellow #FBBF24 / rgb(251,191,36)).
 */
export const REACH_TIER_0_AMBIENT_GRADIENT =
  'linear-gradient(135deg, rgba(139,0,255,0.12) 0%, rgba(251,191,36,0.12) 100%)';

interface HowToReachTierTier0Props {
  onBack: () => void;
  /** This machine's scores compared with the next tier (Tier 1). */
  componentChecks: ComponentCheck[];
  /** Disclosed eligibility rows. OS is merged with the hardware OS row. */
  conditions?: HostEligibilityCondition[];
}

/**
 * Tier 0 onboarding “how to unlock” — same reach-tier card + machine check rows as `HowToReachTier`.
 */
export const HowToReachTierTier0 = ({ onBack, componentChecks, conditions }: HowToReachTierTier0Props) => {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const headline = TIER_0_WELCOME_HEADLINE;
  const intro = TIER_0_UNLOCK_INTRO_COPY;
  const passLabel = 'Pass';
  const failLabel = 'Fail';

  const checks = useMemo(
    () => mergeUnlockChecks(componentChecks, conditions),
    [componentChecks, conditions],
  );

  const toggleExpand = (key: string) => {
    setExpandedKey((prev) => (prev === key ? null : key));
  };

  return (
    <RewardsSubpageChrome onBack={onBack}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', alignItems: 'stretch' }}>
        <ReachTierDetailCard
          borderGradient={REACH_TIER_0_BORDER_GRADIENT}
          ambientGradient={REACH_TIER_0_AMBIENT_GRADIENT}
        >
          <p style={{ margin: '0 0 6px', color: '#ffffff', fontSize: 18, lineHeight: 1.15, fontWeight: 700 }}>
            {headline}
          </p>

          <p style={{ margin: '0 0 12px', color: '#8d8aa8', fontSize: 12, lineHeight: '15px', fontWeight: 500 }}>
            {intro}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
            {checks.length === 0 && (
              <p style={{ margin: 0, color: '#8d8aa8', fontSize: 12, lineHeight: '15px' }}>
                This machine’s scores are not available yet.
              </p>
            )}
            {checks.map((check) => (
              <MachineCheckRow
                key={check.component}
                check={check}
                isExpanded={expandedKey === check.component}
                onToggle={() => toggleExpand(check.component)}
                passLabel={passLabel}
                failLabel={failLabel}
                failHintText={check.failHint}
              />
            ))}
          </div>
        </ReachTierDetailCard>
      </div>
    </RewardsSubpageChrome>
  );
};
